// ============================================================================
// PairsForm — Configuration du Menu (Phase 1 refonte navigation — 2026-08-05 ;
// palier 0 du lot 9 « menu par casquette » — 2026-10-17)
// ============================================================================
// Modèle : DOMAINES DE GESTION (pilote/super-admin) + ESPACE PERSONNEL par
// casquette. Cible : docs/Redesign UI-UX/MENU_CIBLE.md §3 et §4 révisés
// (arbitrage FX 2026-10-14) ; ordre de travail : docs/plans/PLAN_REFACTORING.md
// §Lot 9 — palier 0 = menu seul (2026-10-17), lot 9.5 = menu FINAL sur les
// pages à onglets (Animateurs, Bibliothèque, Projets, Programmation) et les
// quatre niveaux d'URL (2026-10-18).
//
//   ─ Pilotage (PILOTE+ × type d'orga) ─
//   ① Tableau de bord (cockpit de pilotage)
//   ② Projets (Intra)
//   ③ Formation (section propre de l'org cecodev, lot 9.5) · Inter
//            (exploitation : vue d'ensemble, programmation — module de l'org
//            marketplace /o/:slug/programmation —, inscriptions, suivi
//            financier, tarifs & réglages)
//   ④ Personnes               Animateurs (page à onglets : annuaire, pool,
//                             certifications, lettres, disponibilités) · Participants
//   ⑤ Bibliothèque            UNE entrée (page à onglets) : les 6 familles
//                             héritables + tarifs + savoir métier
//   ⑥a Qualité & impact       Qualiopi · satisfaction · impact (suivi) · BPF
//   ⑥b Contrats & factures    reporting · facturation client · paiements · rému
//   ⑦ Administration plateforme (pilote Cecodev) — affaires, portefeuille,
//            organisations, outils IA, OPCO, journal des envois, support
//   ⚙ Paramètres              organisation · automatisations · cockpits · équipe & rôles
//   ─ Espace personnel (les casquettes s'emboîtent : participant ⊂ animateur ⊂ pilote) ─
//   • Mon espace          Accueil · Mes échanges · Mon profil
//   • Ma formation        Mes inscriptions (onglets : liste · Suivi Inter · Documents)
//                         · E-Learning · Groupes d'entraînement · les deux
//                         catalogues · Devenir animateur (Inter)
//   • Mon espace animateur (badge ou fiche requis) Mon dossier · Mes
//                         positionnements · Proposer un événement · Mes groupes
//                         · Mes outils · Mes comptes clients
//
// Règle de tri (MENU_CIBLE §2) : est-ce que j'y vais parce que c'est À MOI
// (je participe, j'anime, je me forme), ou parce que J'ADMINISTRE une
// organisation ? La réponse détermine l'espace, quel que soit le rôle.
// Le sélecteur d'organisation est masqué sur les routes personnelles (Sidebar).
// Tout est gouverné par la matrice RBAC via can(ctx, key) : un item dont la
// permission est absente est masqué automatiquement.
//
// Historique : fusion des ex-sections « Mon planning » / « Animer » / « Mon
// espace » / « M'inscrire » / « Mon compte » (modèle N55 « domaines +
// casquettes », 2026-06-14 → refonte Phase 1, 2026-08-05 → casquettes
// emboîtées, 2026-10-17).
// ============================================================================

import {
  Home,
  FolderKanban,
  FolderOpen,
  Users,
  UserCog,
  Scale,
  Settings,
  ClipboardList,
  Gauge,
  Shield,
  ShieldCheck,
  Building,
  Package,
  FlaskConical,
  FileText,
  User,
  GraduationCap,
  BarChart3,
  Mic,
  FolderCheck,
  Receipt,
  FileSignature,
  Store,
  MessageCircle,
  CalendarSearch,
  Target,
  BrainCircuit,
  UserPlus,
  AlertTriangle,
  CreditCard,
  Sparkles,
  Zap,
  Briefcase,
  Landmark,
  MailCheck,
  DoorOpen,
} from "lucide-react";

import type { MenuItem, MenuSection, NavContext, RecentProject } from "./types";
import { segmentOrg, urlOrg } from "../urls";

// ----------------------------------------------------------------------------
// Helper permissions
// ----------------------------------------------------------------------------
// Court-circuite à true pour SUPER_ADMIN (cohérent avec le service backend).
// Renvoie false si les permissions ne sont pas encore chargées —
// comportement "refus par défaut" préférable au flash d'item bloqué.

function can(ctx: NavContext, key: string): boolean {
  const allRoles = ctx.roles && ctx.roles.length > 0 ? ctx.roles : [ctx.role];
  if (allRoles.includes("SUPER_ADMIN")) return true;
  return ctx.allowedPermissions?.has(key) ?? false;
}

// ----------------------------------------------------------------------------
// Union des casquettes — ESPACES PERSONNELS UNIQUEMENT (FX 2026-08-19)
// ----------------------------------------------------------------------------
// Une personne peut être participante d'une org et animatrice d'une autre
// (alumni Cecodev → animateur Cecodev_Inter ; stagiaire Module Vert → animateur
// de son employeur Intra). Elle ne connaît pas nos organisations et n'a pas à
// les connaître : son menu personnel est l'UNION de ses casquettes, et l'org
// active bascule toute seule quand elle entre dans l'espace d'une autre
// (`useCasquetteImplicite`).
//
// ⚠️ Frontière stricte : réservé aux sections « Mon espace », « Ma formation »
// et « Mon espace animateur ». Les sections d'ADMINISTRATION (pilotage,
// personnes, finances, plateforme, admin) restent gouvernées par `can()`, donc
// par la seule org active — un pilote ne doit jamais voir le menu d'admin d'une
// autre org sans avoir choisi d'y entrer.

/** Une des clés est-elle portée par l'org active OU par une autre casquette ? */
function canU(ctx: NavContext, ...keys: string[]): boolean {
  if (keys.some((k) => can(ctx, k))) return true;
  return (ctx.casquettes ?? []).some((c) => keys.some((k) => c.permissions.has(k)));
}

/**
 * Membership à activer pour exercer l'une de ces clés. `undefined` quand l'org
 * active suffit déjà (cas nominal) ou quand personne ne la porte.
 */
function membershipPour(ctx: NavContext, ...keys: string[]): string | undefined {
  if (keys.some((k) => can(ctx, k))) return undefined;
  return (ctx.casquettes ?? []).find((c) => keys.some((k) => c.permissions.has(k)))?.membershipId;
}

/** Membership d'une casquette portée par une org d'un TYPE donné (ex. CECODEV). */
function membershipTypeOrg(ctx: NavContext, type: string): string | undefined {
  if (ctx.organisationType === type) return undefined;
  return (ctx.casquettes ?? []).find((c) => c.organisationType === type)?.membershipId;
}

/** La personne a-t-elle une casquette dans une org de ce type ? */
function aTypeOrg(ctx: NavContext, type: string): boolean {
  return ctx.organisationType === type || (ctx.casquettes ?? []).some((c) => c.organisationType === type);
}

const SEP = (id: string, label: string): MenuItem => ({
  id,
  label,
  href: "#",
  disabled: true,
});

// ----------------------------------------------------------------------------
// Construction du menu
// ----------------------------------------------------------------------------

export function buildMenu(ctx: NavContext, recentProjects?: RecentProject[]): MenuSection[] {
  // Lot 9.1 : les liens du niveau organisation portent l'org du contexte (/o/:slug).
  const seg = segmentOrg(ctx.organisationSlug, ctx.organisationId);
  const { role, roles, organisationType, estAnimateur, estParticipant, plateformeActif } = ctx;
  const sections: MenuSection[] = [];
  const allRoles = roles && roles.length > 0 ? roles : [role];

  const isAnimateur = allRoles.includes("ANIMATEUR") || estAnimateur;
  const isPilote = allRoles.includes("PILOTE");
  const isSuperAdmin = allRoles.includes("SUPER_ADMIN");
  const isPiloteCecodev = isSuperAdmin || (isPilote && organisationType === "CECODEV");
  // La plateforme Inter est OPÉRÉE par Cecodev_Inter (org type INTER) autant que
  // gouvernée par Cecodev → le pilote des DEUX orgs voit la section « Inter ».
  const isPilotePlateforme =
    isSuperAdmin || (isPilote && (organisationType === "CECODEV" || organisationType === "INTER"));
  // Gate transverse de l'administration d'une orga (Pilote Intra + Cecodev)
  const peutAdminOrga = can(ctx, "ADMIN.users.list");
  // « Relève de l'animation » (retour FX 2026-08-19, règle unique partagée avec
  // `useCandidatureAnimateur`) : une fiche animateur ACTIVE ou un badge reconnu
  // à l'annuaire. Sans l'un des deux, pas d'espace animateur — et pas
  // d'invitation à candidater que la personne ne pourrait pas honorer.
  const releveDeLAnimation = isAnimateur || !!ctx.niveauCertification;
  // Divulgation progressive (FX 2026-08-18, révisée 2026-10-14) : les entrées
  // qui ne parlent que d'un contenu encore inexistant — échanges, documents,
  // suivi Inter — attendent la première inscription (`aInscriptions`, porté par
  // la session) et ne repartent plus. « Mes inscriptions » fait exception
  // depuis le 2026-10-14 : toujours visible, sa page vide renvoie aux deux
  // catalogues (c'est la porte d'entrée, pas un écran vide).
  const aDuContenuParticipant = ctx.aInscriptions === true;

  // ════════════════════════════════════════════════════════════════════════
  // ① TABLEAU DE BORD — cockpit de PILOTAGE (org-scopé, /mes-actions)
  // ════════════════════════════════════════════════════════════════════════
  // Depuis la Phase 1 (2026-08-05), l'accueil PERSONNEL est « Mon espace ›
  // Accueil » (/mes-seances) : le Tableau de bord ne sert plus que le
  // pilotage, donc les pilotes. Les animateurs purs ont leur cockpit self
  // (CR à déposer, lettres à accepter) en bandeau de l'Accueil perso
  // (commit 4) + la cloche de notifications.
  // Reste un LIEN DIRECT (palier 0, 2026-10-17) : une section du menu est soit
  // un lien, soit une liste — le cockpit garde son clic unique et son badge ;
  // les Affaires (ex-section « Commercial ») ont rejoint ⑦.

  if (isPilote || isSuperAdmin) {
    sections.push({
      id: "dashboard",
      title: "Tableau de bord",
      icon: Home,
      href: urlOrg("", seg),
    });
  }

  // ════════════════════════════════════════════════════════════════════════
  // ② PROJETS — pilotage Intra (projet › produit › groupe › séance)
  // ════════════════════════════════════════════════════════════════════════

  // « Trois portes » (FX 2026-08-13) : les orgs CECODEV / INTER sont 100 %
  // Inter — la section Projets disparaît de leur menu (l'Intra ponctuel de
  // la SAS passe par l'org cecodev-intra). Un pilote Cecodev IMMERGÉ dans
  // une org cliente (type CLIENT_INTRA) la retrouve normalement. La vue
  // transverse « Projets » (ci-dessous) remplace l'accès direct.
  // CECODEV masquée à son tour (lot 8.2, décision A FX 2026-10-17) : les
  // Modules Vert / Rouge et la supervision vivent dans le hub Formation et son
  // catalogue public dédié — plus aucun projet à piloter dans l'org cecodev,
  // dont les deux projets sont des conteneurs techniques (INTER / FORMATION,
  // `utils/projet-hote.ts`). Les URL /projets restent joignables, seul le menu
  // change.
  const orgInterOnly = organisationType === "INTER" || organisationType === "CECODEV";
  // Section de PILOTAGE (FX 2026-10-13) : la clé INTRA.projet.list, aussi
  // accordée aux animateurs Intra par la matrice, ouvrait « Projets » à des
  // animateurs — « Mes projets » et « Disponibilité animateurs » leur répondaient
  // « Accès non autorisé ». Leurs groupes vivent dans « Mon espace animateur ».
  if (!orgInterOnly && (isPilote || isSuperAdmin) && (can(ctx, "INTRA.projet.list") || can(ctx, "INTRA.projet.create"))) {
    const projetsItems: MenuItem[] = [];
    // UNE entrée (lot 9.5, décision 4 du 2026-10-14) : « Mes projets » et la
    // vue transverse « Tous les projets » du pilote Cecodev (FX 2026-08-17 :
    // toutes les orgs clientes, ouverture dans l'org du projet) sont les deux
    // onglets de /o/:slug/projets — le filtre « les miens / tous » est dans la
    // page, le menu n'en sait rien.
    if (can(ctx, "INTRA.projet.list")) {
      projetsItems.push({ id: "projets-tous", label: "Projets", href: urlOrg("/projets", seg), icon: FolderOpen });
    }
    // « Nouveau projet » et « Exports » ont quitté le menu (retour FX
    // 2026-08-13 : actions rares, elles vivent là où on en a besoin —
    // bouton « Nouveau projet » sur /projets, « Exporter les données »
    // dans le menu ⋮ du tableau de bord projet). « Disponibilité animateurs »
    // est un onglet de la page Animateurs (lot 9.5).
    if (can(ctx, "INTRA.projet.list") && recentProjects && recentProjects.length > 0) {
      projetsItems.push(SEP("projets-sep-recents", "───"));
      recentProjects.slice(0, 4).forEach((p) => {
        projetsItems.push({ id: `projet-${p.id}`, label: p.nom, href: urlOrg(`/projets/${p.id}`, seg), icon: FolderKanban });
      });
    }
    if (projetsItems.length > 0) {
      sections.push({ id: "projets", title: "Projets", icon: FolderKanban, items: projetsItems });
    }
  }

  // ════════════════════════════════════════════════════════════════════════
  // ③ INTER — exploitation (catalogue public + groupes + inscription)
  // Pilote de Cecodev (gouvernance) OU de Cecodev_Inter (exploitation).
  // ════════════════════════════════════════════════════════════════════════

  // Décision 6 du 2026-10-14 (lot 9.5) : la Formation d'animateurs est une
  // section PROPRE quand l'org active est cecodev — « une orga = un mode »,
  // visible dans le menu ; lien direct vers le hub /o/cecodev/formation.
  if (organisationType === "CECODEV" && (isPilote || isSuperAdmin)) {
    sections.push({ id: "formation", title: "Formation", icon: GraduationCap, href: urlOrg("/formation", seg) });
  }

  if (isPilotePlateforme) {
    const plateformeItems: MenuItem[] = [];
    // Chaque entrée de cette section vit dans UNE org précise : la Formation
    // d'animateurs est opérée par `cecodev`, l'Animation par la marketplace
    // `cecodev-inter`. Ces pages lisent donc l'org ACTIVE, et c'est au pilote
    // de choisir dans quelle org il travaille.
    //
    // ⚠️ NE PAS ré-introduire de bascule automatique ici (essayée puis RETIRÉE
    // le 2026-08-19, cf. JOURNAL) : déplacer l'org active sous les pieds d'un
    // administrateur rend son contexte illisible — il ne sait plus dans quelle
    // org il se trouve, donc plus ce que ses gestes suivants affecteront. Le
    // besoin réel — « je ne vois rien et je ne sais pas pourquoi » — est traité
    // par un BANDEAU explicite sur la page concernée, avec un bouton de
    // bascule. Signaler, pas décider à la place.
    // Le hub Formation d'animateurs (org cecodev — N4 « trois portes », FX
    // 2026-08-13) a sa section propre ci-dessus depuis le lot 9.5 : « Inter »
    // ne porte plus que l'exploitation (décision 6 du 2026-10-14).
    // ── Bloc 1 : Pilotage (le quotidien des groupes ouverts) ──────────────
    // N4 ③ : les entrées Animation portent `?vitrine=codev` — la porte
    // Animation ne montre que la marketplace (les sessions Formation ont leur
    // porte à elles, boutons du hub /formation). Les deep-links SANS vitrine
    // (CTA cockpit, favoris) restent en pot commun.
    if (can(ctx, "INTER.dashboard_admin.view")) {
      plateformeItems.push({ id: "plateforme-dashboard", label: "Vue d'ensemble", href: "/plateforme/dashboard?vitrine=codev", icon: BarChart3 });
    }
    if (can(ctx, "INTER.reglages.gerer")) {
      // UNE entrée « Programmation » (FX 2026-08-13 : Pipeline d'offre et
      // Axes se recoupaient) : le Pipeline EST l'écran de programmation, la
      // liste des axes se gère par son bouton « Gérer les axes ».
      // Lot 9.5 : module de l'org marketplace (/o/:slug/programmation, modèle
      // §8.3) — depuis cecodev, la page navigue vers /o/cecodev-inter/… et la
      // garde de route bascule le jeton, en le disant.
      plateformeItems.push({ id: "plateforme-pipeline", label: "Programmation", href: urlOrg("/programmation", seg), icon: CalendarSearch });
    }
    if (can(ctx, "INTER.dashboard_admin.view")) {
      plateformeItems.push({ id: "plateforme-beneficiaires", label: "Inscriptions & bénéficiaires", href: "/plateforme/beneficiaires?vitrine=codev", icon: Users });
    }
    // Suivi financier + Tarifs & réglages : remontés de l'Administration
    // plateforme au hub Inter (FX 2026-08-13 — la configuration vit avec
    // l'exploitation).
    if (can(ctx, "INTER.reglages.gerer")) {
      // Note utile si la question du scoping revient : `config_tarifs_inter` et
      // `inter_reglages` sont des tables GLOBALES (aucune colonne
      // `organisation_id`) — ces écrans affichent la même chose depuis
      // n'importe quelle org.
      //
      // « Suivi financier » (FX 2026-08-30, allègement du menu) : la page
      // Marges, qui porte un bouton « Régler les tarifs » vers
      // /admin/plateforme/tarifs. Pilote Cecodev seulement (accès marges).
      if (isPiloteCecodev) {
        plateformeItems.push({ id: "plateforme-marges", label: "Suivi financier", href: "/plateforme/marges", icon: Scale });
      }
      // « Tarifs & réglages » — UNE entrée (palier 0 du lot 9, FX 2026-10-14)
      // qui remplace le duo Tarifs / Réglages : la page Réglages porte le lien
      // vers la grille des tarifs par badge (et retour) — la route
      // /admin/plateforme/tarifs reste servie, hors menu, pour tous.
      plateformeItems.push({ id: "plateforme-tarifs-reglages", label: "Tarifs & réglages", href: "/plateforme/relances-debits", icon: Settings });
    }
    // Hors menu (accès contextuel) :
    // - « Groupes & cycles » est DISSOUTE : le cycle de vie des groupes se gère
    //   dans la Vue d'ensemble (table) + le Pipeline (cockpit). La page
    //   /admin/plateforme ne sert plus qu'aux réglages (tarifs/factures/auto).
    // - « Déclarations d'absence » (anecdotique) : retirée du menu, accessible
    //   via la Vue d'ensemble (/admin/plateforme/absences, route conservée).
    // - « Axes stratégiques du catalogue » : bouton « Gérer les axes » du Pipeline.
    // - « Propositions de groupes » : repliée dans le cockpit du Pipeline.
    // - « Tarifs » (grille par badge) : bouton de Suivi financier et de
    //   Tarifs & réglages.
    if (plateformeItems.length > 0) {
      // « Inter » (FX 2026-08-13) : Formation d'animateurs + Animation.
      sections.push({ id: "plateforme", title: "Inter", icon: Store, items: plateformeItems });
    }
  }

  // ── Vue transverse sur les orgs 100 % Inter (FX 2026-08-17) : la section ②
  // Projets y est absente — on offre quand même l'accès transverse au pilote
  // Cecodev (tous les projets actifs des orgs clientes, clic = immersion —
  // « on opère dans une org, on observe en transverse »).
  // Lot 9.5 : UNE entrée, lien direct vers l'onglet « Tous les projets
  // clients » de /o/:slug/projets (la page s'ouvre dessus dans une org Inter).
  if (isPiloteCecodev && orgInterOnly) {
    sections.push({ id: "projets-intra", title: "Projets", icon: FolderKanban, href: urlOrg("/projets?tab=tous", seg) });
  }

  // ════════════════════════════════════════════════════════════════════════
  // ④ PERSONNES — animateurs + participants + pool (transverse Intra/Inter)
  // ════════════════════════════════════════════════════════════════════════
  // « Équipe & rôles » a rejoint ⚙ Paramètres (décision 2 du 2026-10-14) :
  // ce sont les droits de l'organisation, pas un annuaire. Lot 9.5 : la page
  // Animateurs à onglets (annuaire, candidatures, conformité, validation,
  // certifications, lettres de mission, disponibilités — décision 1) porte
  // tout le pool ; ses onglets sont gatés dans la page comme l'étaient les
  // entrées (org plateforme, pilote Cecodev). Deux entrées : Animateurs,
  // Participants.

  if (peutAdminOrga) {
    const personnesItems: MenuItem[] = [];
    // Annuaires de base (toute orga)
    personnesItems.push({ id: "personnes-animateurs", label: "Animateurs", href: urlOrg("/animateurs", seg), icon: UserCog });
    personnesItems.push({ id: "personnes-participants", label: "Participants", href: urlOrg("/participants", seg), icon: Users });


    sections.push({ id: "personnes", title: "Personnes", icon: Users, items: personnesItems });
  }

  // ════════════════════════════════════════════════════════════════════════
  // ⑤ BIBLIOTHÈQUE — les 6 familles héritables + tarifs + savoir métier
  // ════════════════════════════════════════════════════════════════════════
  // UNE entrée (lot 9.5, décision 3 du 2026-10-14) : la Bibliothèque à
  // onglets — catalogue produits, tarifs (D3 : LA page canonique de
  // tarification, org-scopée), modèles d'emails, modèles de documents (fork
  // CECODEV / INTER, masters en immersion `standards` ; les clients Intra
  // héritent sans forker), questionnaires Qualiopi, protocoles d'impact (les
  // MODÈLES — le suivi des campagnes vit sous Qualité & impact), prompts de
  // bilan, savoir métier Codev (org cecodev seulement, FX 2026-08-23). Les
  // onglets sont gatés dans la page comme l'étaient les entrées.
  if (peutAdminOrga) {
    sections.push({ id: "bibliotheque", title: "Bibliothèque", icon: Package, href: urlOrg("/bibliotheque", seg) });
  }

  // ════════════════════════════════════════════════════════════════════════
  // ⑥a QUALITÉ & IMPACT — Qualiopi, satisfaction, mesure d'impact, BPF
  // ⑥b CONTRATS & FACTURES — facturation client, paiements, rémunération
  // ════════════════════════════════════════════════════════════════════════
  // Scission Phase 2 (MENU_CIBLE §4, 2026-08-05) : l'ex-« Finances &
  // conformité » mélangeait deux métiers, deux publics, deux rythmes.

  {
    const qiItems: MenuItem[] = [];
    if (can(ctx, "QUALIOPI.indicateurs.consulter")) {
      qiItems.push({ id: "fc-qualiopi", label: "Suivi Qualiopi", href: urlOrg("/qualite", seg), icon: ShieldCheck });
      qiItems.push({ id: "fc-satisfaction", label: "Satisfaction (questionnaires)", href: urlOrg("/satisfaction", seg), icon: Gauge });
    }
    if (peutAdminOrga) {
      // SUIVI des campagnes d'impact (les MODÈLES/protocoles restent en ⑤).
      qiItems.push({ id: "qi-impact", label: "Mesure d'impact (suivi)", href: urlOrg("/projets/mesure-impact", seg), icon: FlaskConical });
    }
    // BPF : de retour sous « Qualité & impact » (décision 5 du 2026-10-14 —
    // cible d'août rétablie : c'est un bilan déclaratif de l'activité de
    // formation, il se lit avec les indicateurs Qualiopi). Id conservé.
    if (can(ctx, "QUALIOPI.bpf.consulter")) {
      qiItems.push({ id: "fc-bpf", label: "BPF", href: urlOrg("/bpf", seg), icon: Receipt });
    }
    if (qiItems.length > 0) {
      sections.push({ id: "qualite-impact", title: "Qualité & impact", icon: ShieldCheck, items: qiItems });
    }
  }

  {
    const cfItems: MenuItem[] = [];
    if (can(ctx, "ADMIN.reporting_financier.view")) {
      cfItems.push({ id: "fc-reporting", label: "Reporting financier", href: urlOrg("/reporting-financier", seg), icon: BarChart3 });
    }
    if (isPilotePlateforme) {
      cfItems.push({ id: "fc-factures-clients", label: "Facturation clients (B2B)", href: "/plateforme/factures-clients", icon: FileText });
      cfItems.push({ id: "fc-paiements", label: "Paiements Inter", href: "/plateforme/paiements", icon: CreditCard });
      // Déplacée depuis ④ Personnes (scission Phase 2) — id conservé.
      cfItems.push({ id: "personnes-remuneration", label: "Rémunération animateurs", href: "/plateforme/remuneration", icon: Receipt });
      // Déplacée depuis ④ Personnes → Pool (FX 2026-08-30 : une lettre de
      // mission est un contrat, elle se cherche ici) — id conservé.
      cfItems.push({ id: "personnes-lettres", label: "Lettres de mission", href: urlOrg("/animateurs?tab=lettres-mission", seg), icon: FileSignature });
    }
    if (cfItems.length > 0) {
      sections.push({ id: "contrats-factures", title: "Contrats & factures", icon: Receipt, items: cfItems });
    }
  }

  // ════════════════════════════════════════════════════════════════════════
  // ⑦ ADMINISTRATION PLATEFORME — gouvernance Cecodev (pilote Cecodev)
  // ════════════════════════════════════════════════════════════════════════

  if (isPiloteCecodev) {
    const apItems: MenuItem[] = [];
    // CRM Cecodev (chantier CRM C1, FX 2026-08-12) : « Affaires » (jamais
    // « Pipeline », homonyme du pipeline d'offre Inter) + « Portefeuille
    // clients » (S1, la relation pluriannuelle). Vue plateforme — la clé CRM.*
    // est absente de la matrice des pilotes clients. Ex-section « Commercial »
    // rangée ici au palier 0 du lot 9 (décision 7 du 2026-10-14 : « se loge
    // dans le Tableau de bord ou l'Administration plateforme » ; le Tableau de
    // bord reste un lien direct). Ids conservés : badge `commercial-affaires`.
    if (can(ctx, "CRM.affaires.view")) {
      apItems.push({ id: "commercial-affaires", label: "Affaires", href: "/plateforme/crm/affaires", icon: Briefcase });
      apItems.push({ id: "commercial-portefeuille", label: "Portefeuille clients", href: "/plateforme/crm/comptes", icon: Users });
      apItems.push(SEP("ap-sep-gouvernance", "───"));
    }
    if (can(ctx, "ADMIN.organisations.list")) {
      apItems.push({ id: "ap-organisations", label: "Organisations clientes", href: "/plateforme/organisations", icon: Building });
      // « Demandes de création » et « Documents légaux (CGV/RI) » retirés
      // (FX 2026-08-31) : les orgs sont créées par le pilote Cecodev (guichet
      // Intra / admin), et les CGV/RI vivent sur cecodev.fr (liens du footer).
    }
    // « Questionnaires d'impact (Standard) » retiré (N198) : l'entrée pointait
    // la MÊME page que « Protocoles d'impact (modèles) » de Catalogue &
    // contenus — deux libellés pour un seul écran. La page est org-scopée :
    // les masters Standard s'éditent en immersion `standards`, comme les
    // autres familles héritables (doctrine D3).
    if (can(ctx, "ADMIN.permissions.editer")) {
      apItems.push({ id: "ap-outils-ia", label: "Matrice outils IA", href: "/plateforme/outils-ia", icon: Sparkles });
    }
    if (can(ctx, "ADMIN.analytics.view")) {
      // « Analytics » retiré (FX 2026-08-31) : le vrai dashboard opérations /
      // finances viendra plus tard ; la détection de code mort passe par le
      // compteur usage_runtime (rapport à la demande), pas par un écran.
      apItems.push({ id: "ap-referent-ia", label: "Référent IA", href: "/plateforme/referent-ia", icon: BrainCircuit });
    }
    // Financeurs OPCO (0253) : référentiel + suivi OPCO par OPCO — vue
    // plateforme (subrogations, accords de prise en charge, factures).
    apItems.push({ id: "ap-opcos", label: "Financeurs OPCO", href: "/plateforme/opcos", icon: Landmark });
    // Journal global des envois (emails + SMS, toutes orgs) — SUPER_ADMIN
    // strict : le serveur refuse tout autre rôle (vue plateforme sensible).
    if (isSuperAdmin) {
      apItems.push({ id: "ap-journal-envois", label: "Journal des envois", href: "/plateforme/journal-envois", icon: MailCheck });
    }
    if (can(ctx, "SUPPORT.toutes_demandes.consulter")) {
      // Les retours bêta vivent dans l'onglet « Suggestions » de cette page
      // (fusion FX 2026-08-31 — pas d'entrée de menu dédiée).
      apItems.push({ id: "ap-support", label: "Support & contact", href: "/plateforme/support", icon: MessageCircle });
    }
    // Tarifs + Réglages Inter : repartis au hub « Inter » (FX 2026-08-13 —
    // l'Administration plateforme ne garde que la gouvernance transverse).
    if (apItems.length > 0) {
      sections.push({ id: "admin-plateforme", title: "Administration plateforme", icon: Shield, separator: true, items: apItems });
    }
  }

  // ════════════════════════════════════════════════════════════════════════
  // ⚙ PARAMÈTRES DE L'ORGANISATION — identité légale, sites, email, IA,
  // automatisations, cockpits, équipe & rôles
  // ════════════════════════════════════════════════════════════════════════

  if (peutAdminOrga) {
    sections.push({
      id: "parametres",
      title: "Paramètres",
      icon: Settings,
      items: [
        { id: "parametres-orga", label: "Organisation", href: urlOrg("/parametres", seg), icon: Settings },
        // Lot L des retours bêta (2026-10-27) : les salles de l'org, leurs
        // blocages et leur planning — réservées par les séances.
        { id: "parametres-salles", label: "Sites et salles", href: urlOrg("/salles", seg), icon: DoorOpen },
        // Centre de réglage des automatisations (Phase 2, 2026-08-05) —
        // horaires org + automatisations par produit + dérogations projet.
        { id: "parametres-automatisations", label: "Automatisations", href: urlOrg("/automatisations", seg), icon: Zap },
        { id: "parametres-cockpits", label: "Cockpits (alertes)", href: urlOrg("/cockpit-reglages", seg), icon: AlertTriangle },
        // Déplacée depuis ④ Personnes (décision 2 du 2026-10-14 : les droits
        // de l'organisation se règlent avec ses paramètres) — id conservé.
        { id: "personnes-equipe", label: "Équipe & rôles", href: urlOrg("/equipe", seg), icon: Shield },
      ],
    });
  }

  // ════════════════════════════════════════════════════════════════════════
  // MON ESPACE — ce que JE fais, quelle que soit ma casquette
  // ════════════════════════════════════════════════════════════════════════
  // Visible de TOUS les connectés ; le sélecteur d'organisation est masqué sur
  // ces routes (Sidebar). Depuis le 2026-10-14 (casquettes emboîtées), la
  // section ne porte plus que le socle commun : l'accueil, les échanges et le
  // profil — « ce que je suis » ; les inscriptions et les documents vivent
  // dans « Ma formation » (« ce que je suis en train d'apprendre »).

  {
    const monEspaceItems: MenuItem[] = [
      // Accueil personnel canonique : hero prochaine séance + alertes + agenda
      // (arbitrage Q1 : pas d'entrée « Mon agenda » séparée — un seul agenda,
      // filtré par nature, dans l'Accueil).
      { id: "accueil-perso", label: "Accueil", href: "/mon-espace", icon: Home },
    ];
    // Masquée tant que la personne n'a aucune inscription (règle maintenue le
    // 2026-10-14) : les échanges d'un cycle terminé restent utiles, l'entrée
    // ne repart plus. Les pilotes/animateurs gardent leurs entrées : le signal
    // vaut pour la casquette participant.
    if (aDuContenuParticipant && estParticipant) {
      // id `seances-communication` : cible du badge « messages non lus » (useNavigation)
      monEspaceItems.push({ id: "seances-communication", label: "Mes échanges", href: "/mon-espace/echanges", icon: MessageCircle });
    }
    monEspaceItems.push({ id: "compte-profil", label: "Mon profil", href: "/mon-espace/profil", icon: User });

    sections.push({ id: "mon-espace", title: "Mon espace", icon: User, separator: true, items: monEspaceItems });
  }

  // ════════════════════════════════════════════════════════════════════════
  // MA FORMATION — je participe, je me forme, je cherche un groupe (tous)
  // ════════════════════════════════════════════════════════════════════════

  {
    const formationItems: MenuItem[] = [];
    // « Mes inscriptions » (FX 2026-10-14) reprend l'ex-« Mes groupes » et
    // l'ex-« Mes inscriptions » de Mon espace : TOUJOURS visible — sa page
    // vide renvoie aux deux catalogues, c'est la porte d'entrée du participant.
    // Palier 0 : la page /mes-groupes (liste Intra + Inter) porte le libellé ;
    // la fusion avec le suivi Inter et les documents (onglets) est le lot 9.
    // Masquage INTER levé (arbitrage Q4, 2026-08-05) : /auth/me/dashboard est
    // person-scopé (fiches par userId, Intra + Inter depuis l'unification 7.C).
    formationItems.push({
      id: "mes-inscriptions",
      label: "Mes inscriptions",
      href: "/mon-espace/inscriptions",
      icon: FolderCheck,
      membershipId: membershipPour(ctx, "PARTICIPANT.mes_seances.view"),
    });
    // Lot 9.3b, puis 10.5 : le suivi Inter (contrats, carte, tunnel à
    // reprendre) vit sur la carte de chaque inscription, mes paiements et mes
    // documents sont des ONGLETS de « Mes inscriptions »
    // (/mon-espace/inscriptions?tab=paiements · ?tab=documents) — plus
    // d'entrées de menu propres ; les anciennes adresses redirigent.
    // E-Learning : contenu de la maison mère. Porté par la casquette Cecodev,
    // même quand l'org active est Cecodev_Inter (un alumni devenu animateur ne
    // doit pas perdre son e-learning en changeant de casquette) — ET par le
    // parcours ouvert (lot 5 certifications, 2026-10-06 : dès la première
    // journée d'un Module, à vie, quelle que soit l'org active).
    if ((estParticipant && aTypeOrg(ctx, "CECODEV")) || !!ctx.parcoursElearning?.vert) {
      formationItems.push({
        id: "mesgroupes-elearning",
        label: "E-Learning",
        href: "/mon-espace/e-learning",
        icon: GraduationCap,
        membershipId: membershipTypeOrg(ctx, "CECODEV"),
      });
    }
    // Mise en relation anciens Module Vert/Rouge (E3). L'audience était « tout
    // participant ou animateur », en comptant sur la page pour revérifier
    // l'éligibilité — mais un participant B2C de la marketplace y voyait une
    // entrée qui ne le concernait pas (retour FX 2026-08-19). L'entraînement
    // est un dispositif de la MAISON MÈRE : on le réserve à qui en relève,
    // c'est-à-dire une casquette Cecodev ou un badge reconnu à l'annuaire
    // (un alumni garde son droit même sans membership actif). La page continue
    // de trancher l'éligibilité réelle via /entrainement/eligibilite.
    if ((estParticipant || isAnimateur) && (aTypeOrg(ctx, "CECODEV") || !!ctx.niveauCertification)) {
      formationItems.push({
        id: "entrainement",
        label: "Groupes d'entraînement",
        href: "/mon-espace/entrainement",
        icon: Users,
        membershipId: membershipTypeOrg(ctx, "CECODEV"),
      });
    }
    // Les deux catalogues vivent ICI (FX 2026-08-18), et non plus dans une
    // section « Trouver un groupe » à part : c'est là qu'on les cherche.
    // Libellés du 2026-10-14 : on nomme le CATALOGUE qu'on ouvre — celui des
    // groupes de Codev (la marketplace Inter) et celui des formations du
    // Cecodev — plutôt que l'intention (« Participer… », « Me former… »), qui
    // ne disait pas lequel des deux mondes on ouvrait.
    formationItems.push({
      id: "inscrire-codev",
      label: "Catalogue des groupes de Codev",
      href: "/catalogue/codev",
      icon: Package,
    });
    formationItems.push({
      id: "inscrire-formation",
      label: "Catalogue des formations du Cecodev",
      href: "/catalogue",
      icon: GraduationCap,
    });
    // Candidature Inter — rangée dans « Ma formation » (FX 2026-10-14) : c'est
    // la suite du parcours de formation, pas déjà de l'animation. Même règle
    // que `useCandidatureAnimateur` (le bouton « + », la carte d'accueil, la
    // page) : permission ET pas déjà sur la marketplace ET badge ou fiche —
    // sans badge, la porte d'entrée reste le catalogue des formations.
    if (canU(ctx, "ANIMATEUR.candidature.postuler") && !plateformeActif && releveDeLAnimation) {
      formationItems.push({
        id: "compte-devenir-animateur",
        label: "Devenir animateur (Inter)",
        href: "/mon-espace/devenir-animateur",
        icon: UserPlus,
        membershipId: membershipPour(ctx, "ANIMATEUR.candidature.postuler"),
      });
    }
    // « Mon parcours » / « Ma certification » : pas d'entrée tant qu'aucune
    // page n'existe (arbitrage Q6 — pas de rubrique vide ; « Ma certification »
    // reviendra avec la chaîne Module Vert → Badge, cf. BACKLOG).
    sections.push({ id: "ma-formation", title: "Ma formation", icon: GraduationCap, items: formationItems });
  }

  // ════════════════════════════════════════════════════════════════════════
  // MON ESPACE ANIMATEUR — j'anime (badge ou fiche animateur active)
  // ════════════════════════════════════════════════════════════════════════
  // Gate de SECTION (retour FX 2026-08-19 : « en l'absence de certification
  // ou accréditation associée à son compte, il ne doit pas voir la section »).
  // Peu importe quelle clé la déclenchera demain, la règle métier est la même —
  // sans badge, pas d'espace animateur. `isAnimateur` reste une porte à part
  // entière : une fiche animateur active fait foi même si l'annuaire n'a pas
  // (encore) sa ligne.

  if (releveDeLAnimation) {
    const animateurItems: MenuItem[] = [];

    // Mon dossier — page UNIQUE à onglets (Ma situation / Mes documents / Mon
    // entretien / Factures & paiements / Mon BPF). Consolidation 2026-06-25,
    // MAINTENUE par l'arbitrage Q2 (2026-08-05) : pas d'éclatement en entrées
    // ?tab=. Libellé « Mon dossier » (2026-10-14) : la section dit déjà
    // « animateur ».
    const CLES_DOSSIER = [
      "ANIMATEUR.statut.consulter",
      "ANIMATEUR.documents.consulter",
      "ANIMATEUR.documents.uploader",
      "ANIMATEUR.lettre_mission.accepter",
      "ANIMATEUR.contrat_cadre.signer",
      "ANIMATEUR.entretien.realiser",
      "ANIMATEUR.revenus.consulter",
      "ANIMATEUR.facture.deposer",
      "ANIMATEUR.bpf.consulter",
    ];
    const canDossier = canU(ctx, ...CLES_DOSSIER);
    if (canDossier) {
      animateurItems.push({
        id: "compte-dossier",
        label: "Mon dossier",
        href: "/mon-espace/animation/dossier",
        icon: ClipboardList,
        membershipId: membershipPour(ctx, ...CLES_DOSSIER),
      });
    }

    // Union des casquettes : la fiche animateur peut vivre dans une AUTRE org
    // que celle qui est active (alumni Cecodev animateur de Cecodev_Inter).
    // `isAnimateur` est déjà cross-org (session) ; les permissions, non.
    if (isAnimateur && canU(ctx, "ANIMATEUR.groupes.view")) {
      // « Mes propositions » (FX 2026-10-14 ; page à onglets au lot 10.7, N235) :
      // proposer un groupe (planifier le créneau attribué, porte privée hors
      // catalogue), proposer un événement (onglet réservé aux éligibles —
      // `peutProposerEvenements`, calculé serveur, lu par la page elle-même),
      // mes positionnements sur les axes. UNE entrée ; les anciennes adresses
      // redirigent (routes/redirections.ts). « Positionnement » reste le mot
      // d'écran de la file d'attente d'un axe — ni « candidature »
      // (recrutement), ni « Planification » (2026-08-28), ni « inscription »
      // (pris par le participant) ; le code garde `axe_candidatures`.
      animateurItems.push({
        id: "animer-propositions",
        label: "Mes propositions",
        href: "/mon-espace/animation/propositions",
        icon: Target,
        membershipId: membershipPour(ctx, "ANIMATEUR.groupes.view"),
      });
      // « Contacts à valider » reste un bouton de la page Mes groupes
      // (consolidation 2026-06-25, maintenue 2026-10-14) ; son bouton « Proposer
      // un groupe » mène à Mes propositions.
      animateurItems.push({
        id: "animer-groupes",
        label: "Mes groupes",
        href: "/mon-espace/animation/groupes",
        icon: FolderKanban,
        membershipId: membershipPour(ctx, "ANIMATEUR.groupes.view"),
      });
    }

    // « Mes outils » — le hub des outils Codev (karaoké, transcription,
    // correcteur, transcripteur de séance ; cards gatées par la matrice outils
    // IA). Réservé aux animateurs (FX 2026-10-14 : « Mes outils n'est utile
    // que pour les animateurs ») : la condition « casquette Cecodev ou outil IA
    // accordé » de 2026-08-23 tombe, la gate de section suffit. La page reste
    // servie à l'URL pour qui y a droit par la matrice.
    animateurItems.push({
      id: "outils-karaoke",
      label: "Mes outils",
      href: "/mon-espace/outils",
      icon: Mic,
      membershipId: membershipTypeOrg(ctx, "CECODEV"),
    });

    // S2 (2026-08-13) : suivi commercial délégué (Chargé d'affaire) — affiché
    // SEULEMENT si au moins un compte est affecté (retour FX : pas d'entrée de
    // menu vide pour les 99 % de non-chargés d'affaire).
    if (canDossier && ctx.aComptesClientsAffectes) {
      animateurItems.push({ id: "compte-mes-comptes", label: "Mes comptes clients", href: "/mon-espace/animation/comptes-clients", icon: Users });
    }

    // NB : la « Vue d'ensemble » animateur (/espace-animateur) est sortie du
    // menu (arbitrage Q3) — route toujours servie ; son cockpit self est
    // absorbé par l'Accueil perso (commit 4).
    sections.push({ id: "activite-animateur", title: "Mon espace animateur", icon: UserCog, items: animateurItems });
  }

  // Filet de sécurité : si une section ne contient plus que des séparateurs,
  // elle a perdu tout son contenu visible et doit disparaître entièrement.
  return sections.filter((s) => {
    if (!s.items || s.items.length === 0) return true;
    const hasReal = s.items.some((it) => !it.disabled);
    return hasReal;
  });
}

// ----------------------------------------------------------------------------
// Export par défaut
// ----------------------------------------------------------------------------

export default buildMenu;
